import React from 'react';
import { motion } from 'framer-motion';
import { X, Zap, MessageCircle, Clock, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UpgradeModal = ({ isOpen, onClose, onUpgradeSuccess }) => {
  const [loading, setLoading] = React.useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create Stripe SUBSCRIPTION checkout session (recurring monthly)
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout page
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMsg = error.response?.data?.detail || 'Falha ao criar sessão de pagamento. Tente novamente.';
      toast.error(errorMsg);
      setLoading(false);
    }
    // Note: Don't set loading to false on success - user is being redirected
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-xl"
        data-testid="upgrade-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-stone hover:text-forest transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h2 className="font-fraunces text-2xl sm:text-3xl font-semibold text-forest mb-2">
            Seja Premium
          </h2>
          <p className="text-stone">
            Você atingiu o limite diário gratuito
          </p>
        </div>

        {/* Benefits */}
        <div className="space-y-4 mb-8">
          {[
            { icon: <MessageCircle className="w-5 h-5" />, text: 'Conversas ilimitadas' },
            { icon: <Clock className="w-5 h-5" />, text: 'Pratique a qualquer hora' },
            { icon: <Star className="w-5 h-5" />, text: 'Melhore mais rápido' },
            { icon: <Zap className="w-5 h-5" />, text: 'Respostas prioritárias' },
          ].map((benefit, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                {benefit.icon}
              </div>
              <span className="text-forest font-medium">{benefit.text}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <Button
          onClick={handleUpgrade}
          disabled={loading}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-full py-6 text-lg font-medium shadow-lg"
          data-testid="upgrade-btn"
        >
          {loading ? 'Redirecionando para pagamento...' : 'Assinar Premium - R$14,90/mês'}
        </Button>

        <p className="text-center text-xs text-stone mt-4">
          Cancele quando quiser. Pagamento seguro via Stripe.
        </p>
      </motion.div>
    </motion.div>
  );
};

export default UpgradeModal;
